/**
 * WebAuthn API layer (analyst endpoints). Axios over the shared apiClient so
 * the existing token-refresh interceptor and retry policy apply.
 *
 * Two auth contexts:
 *  - Pre-auth (before login completes): the backend uses a `preauth_ticket`
 *    (Redis, 5-min TTL) passed IN THE BODY for POSTs and as a QUERY param for
 *    the GET status call. It is not an auth token and never rides a header.
 *  - Authenticated (device management, after login): `Authorization: ANALYST
 *    <jwt>` from sessionStorage, like every other authenticated call.
 */
import apiClient from "../../../utils/axiosClient";
import { ENDPOINTS, WEBAUTHN_MOCK } from "../config";

// Backend wraps every payload in `response_data`. Return that inner object.
function unwrap(res) {
  const d = res && res.data;
  if (d && typeof d === "object" && "response_data" in d) return d.response_data;
  return d;
}

function sessionHeaders() {
  const token = sessionStorage.getItem("token");
  return token ? { Authorization: `ANALYST ${token}` } : {};
}

// Best-effort default device_type (enum: laptop|desktop|mobile|tablet|other).
// UA can't tell a laptop from a desktop, so computers default to "desktop";
// the enroll UI lets the user correct it. Cosmetic — the binding is the credential.
export function detectDeviceType() {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/ipad|tablet/.test(ua)) return "tablet";
  if (/mobi|iphone|android/.test(ua)) return "mobile";
  return "desktop";
}

function mock() {
  // eslint-disable-next-line global-require
  return require("./mock").mockApi;
}

export const webauthnApi = {
  // ---- authentication (assertion) ----
  authOptions(ticket) {
    if (WEBAUTHN_MOCK) return mock().authOptions();
    return apiClient
      .post(ENDPOINTS.authOptions, { preauth_ticket: ticket })
      .then(unwrap)
      .then((rd) => rd.options);
  },
  authVerify(assertion, fingerprint, ticket) {
    if (WEBAUTHN_MOCK) return mock().authVerify(assertion);
    return apiClient
      .post(ENDPOINTS.authVerify, {
        preauth_ticket: ticket,
        assertion,
        ...(fingerprint ? { device_fingerprint: fingerprint } : {}),
      })
      .then(unwrap);
  },

  // ---- enrollment (attestation): request -> status -> options -> verify ----
  registerRequest(label, deviceType, fingerprint, ticket) {
    if (WEBAUTHN_MOCK) return mock().registerRequest(label);
    return apiClient
      .post(ENDPOINTS.registerRequest, {
        preauth_ticket: ticket,
        label,
        device_type: deviceType || detectDeviceType(),
        ...(fingerprint ? { device_fingerprint: fingerprint } : {}),
      })
      .then(unwrap);
  },
  registerStatus(ticket, requestId) {
    if (WEBAUTHN_MOCK) return mock().registerStatus();
    return apiClient
      .get(ENDPOINTS.registerStatus, {
        params: {
          preauth_ticket: ticket,
          ...(requestId ? { request_id: requestId } : {}),
        },
      })
      .then(unwrap);
  },
  registerOptions(requestId, ticket) {
    if (WEBAUTHN_MOCK) return mock().registerOptions(requestId);
    return apiClient
      .post(ENDPOINTS.registerOptions, { preauth_ticket: ticket, request_id: requestId })
      .then(unwrap)
      .then((rd) => rd.options);
  },
  registerVerify(requestId, attestation, label, fingerprint, ticket) {
    if (WEBAUTHN_MOCK) return mock().registerVerify(requestId, attestation, label);
    return apiClient
      .post(ENDPOINTS.registerVerify, {
        preauth_ticket: ticket,
        request_id: requestId,
        attestation,
        label,
        ...(fingerprint ? { device_fingerprint: fingerprint } : {}),
      })
      .then(unwrap);
  },

  // ---- self-service credential management (authenticated) ----
  listCredentials() {
    if (WEBAUTHN_MOCK) return mock().listCredentials();
    return apiClient
      .get(ENDPOINTS.credentials, { headers: sessionHeaders() })
      .then(unwrap)
      .then((rd) => (rd && rd.credentials) || []);
  },
  renameCredential(id, label) {
    if (WEBAUTHN_MOCK) return mock().renameCredential(id, label);
    return apiClient
      .patch(ENDPOINTS.credentialRename(id), { label }, { headers: sessionHeaders() })
      .then(unwrap);
  },
  removeCredential(id) {
    if (WEBAUTHN_MOCK) return mock().removeCredential(id);
    return apiClient
      .delete(ENDPOINTS.credentialDelete(id), { headers: sessionHeaders() })
      .then(unwrap);
  },
  loginHistory(limit = 20) {
    if (WEBAUTHN_MOCK) return Promise.resolve([]);
    return apiClient
      .get(ENDPOINTS.loginHistory, { params: { limit }, headers: sessionHeaders() })
      .then(unwrap)
      .then((rd) => (rd && rd.login_history) || []);
  },
};
